import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'motion/react'
import { ArrowRight, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { lockScroll } from '@/lib/smooth-scroll'
import { arrowClass, buttonClass } from '@/components/generals/button'
import Magnetic from '@/components/generals/magnetic'
import QuoteLink from '@/components/generals/quote-link'
import SmartLink from '@/components/generals/smart-link'

import {
    contact,
    hero,
    navLinks,
    quoteLabel
} from '@/constants/content'

const Nav: FC = (): ReactNode => {
    const { scrollY } = useScroll()
    const [hidden, setHidden] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)
    const toggleRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    useMotionValueEvent(scrollY, 'change', (latest) => {
        const previous = scrollY.getPrevious() ?? 0

        setScrolled(latest > 24)

        if (latest > previous && latest > 320) setHidden(true)
        else if (latest < previous || latest <= 320) setHidden(false)
    })

    useEffect(() => {
        if (!open) return

        lockScroll(true)
        menuRef.current?.querySelector<HTMLElement>('a, button')?.focus()

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false)
                toggleRef.current?.focus()

                return
            }

            if (event.key !== 'Tab' || !menuRef.current || !toggleRef.current) return

            const focusables = [toggleRef.current, ...Array.from(menuRef.current.querySelectorAll<HTMLElement>('a, button'))]
            const first = focusables[0]
            const last = focusables[focusables.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }

            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', handleKey)

        return () => {
            document.removeEventListener('keydown', handleKey)
            lockScroll(false)
        }
    }, [open])

    useEffect(() => {
        const desktop = window.matchMedia('(min-width: 1024px)')
        const close = () => {
            if (desktop.matches) setOpen(false)
        }

        desktop.addEventListener('change', close)

        return () => desktop.removeEventListener('change', close)
    }, [])

    // Unlock scrolling before the link moves the page, so in-page jumps work straight from the menu.
    const closeMenu = () => {
        lockScroll(false)
        setOpen(false)
    }

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 border-b-2 transition-[translate,background-color,border-color,color] duration-500 ease-snap',
                hidden && !open && '-translate-y-full',
                open ? 'border-transparent text-paper [--focus-ring:var(--paper)]' : 'text-ink',
                !open && scrolled ? 'border-ink bg-paper/90 backdrop-blur-md' : 'border-transparent'
            )}
        >
            <div className='shell flex h-16 items-center justify-between gap-3 lg:grid lg:h-20 lg:grid-cols-[1fr_auto_1fr]'>
                <SmartLink
                    href='/#top'
                    onClick={closeMenu}
                    className='group flex items-center gap-2.5 lg:justify-self-start'
                >
                    <span
                        aria-hidden='true'
                        className='hidden size-9 place-items-center rounded-full border border-ink bg-[linear-gradient(to_top,#21CECD,#22FFB2)] font-heading text-[0.8125rem] font-extrabold text-ink transition-transform duration-700 ease-bounce group-hover:rotate-[360deg] min-[380px]:grid'
                    >
                        MD
                    </span>

                    <span className='font-heading text-lg font-extrabold tracking-[-0.02em] wdth-85 sm:text-xl'>
                        Mudry Design
                    </span>
                </SmartLink>

                <nav aria-label='Main' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {navLinks.map(link => (
                            <li key={link.href}>
                                <SmartLink
                                    href={link.href}
                                    className='group relative inline-flex px-3 py-2 font-heading text-[1.0625rem] font-semibold'
                                >
                                    <span className='relative'>
                                        {link.label}

                                        <span
                                            aria-hidden='true'
                                            className='absolute -bottom-1 left-0 h-[3px] w-full origin-right scale-x-0 rounded-full bg-blaze transition-transform duration-300 ease-snap group-hover:origin-left group-hover:scale-x-100'
                                        />
                                    </span>
                                </SmartLink>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2 sm:gap-3 lg:justify-self-end'>
                    <a
                        href={contact.phoneHref}
                        className='group hidden items-center gap-2 px-2 font-heading text-[1.0625rem] font-semibold lg:inline-flex'
                    >
                        <Phone aria-hidden='true' className='size-4 transition-transform duration-300 ease-bounce group-hover:-rotate-12 group-hover:scale-110' />
                        {contact.phone}
                    </a>

                    <a
                        href={contact.phoneHref}
                        aria-label={hero.call}
                        className={cn(
                            'hidden size-11 place-items-center rounded-lg border-2 border-current lg:hidden',
                            !open && 'min-[440px]:grid'
                        )}
                    >
                        <Phone aria-hidden='true' className='size-4' />
                    </a>

                    {!open && (
                        <Magnetic className='hidden sm:inline-flex'>
                            <QuoteLink className={buttonClass({ size: 'sm', className: 'bg-[#22FFB2]' })}>
                                {quoteLabel}
                                <ArrowRight aria-hidden='true' className={cn(arrowClass, 'size-4')} />
                            </QuoteLink>
                        </Magnetic>
                    )}

                    {!open && (
                        <QuoteLink className={buttonClass({ size: 'sm', shadow: 'none', className: 'bg-[#22FFB2] px-3.5 sm:hidden' })}>
                            {quoteLabel}
                        </QuoteLink>
                    )}

                    <button
                        ref={toggleRef}
                        type='button'
                        aria-expanded={open}
                        aria-controls='site-menu'
                        onClick={() => setOpen(value => !value)}
                        className='relative grid size-11 place-items-center rounded-lg border-2 border-current lg:hidden'
                    >
                        <span className='sr-only'>
                            {open ? 'Close menu' : 'Open menu'}
                        </span>

                        <span
                            aria-hidden='true'
                            className={cn('absolute h-0.5 w-5 rounded-full bg-current transition-transform duration-300 ease-snap', open ? 'rotate-45' : '-translate-y-[5px]')}
                        />

                        <span
                            aria-hidden='true'
                            className={cn('absolute h-0.5 w-5 rounded-full bg-current transition-transform duration-300 ease-snap', open ? '-rotate-45' : 'translate-y-[5px]')}
                        />
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        ref={menuRef}
                        id='site-menu'
                        role='dialog'
                        aria-modal='true'
                        aria-label='Menu'
                        className='fixed inset-0 -z-10 flex flex-col overflow-y-auto bg-ink px-5 pb-10 pt-28 text-paper sm:px-8 lg:hidden'
                        initial={{ clipPath: 'circle(0% at 92% 2rem)' }}
                        animate={{ clipPath: 'circle(150% at 92% 2rem)' }}
                        exit={{ clipPath: 'circle(0% at 92% 2rem)' }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <nav aria-label='Mobile'>
                            <ul className='flex flex-col'>
                                {navLinks.map((link, index) => (
                                    <motion.li
                                        key={link.href}
                                        initial={{ x: -48, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: 0.15 + index * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                        className='border-b border-paper/15'
                                    >
                                        <SmartLink
                                            href={link.href}
                                            onClick={closeMenu}
                                            className='flex items-center justify-between py-4 font-heading text-5xl font-extrabold tracking-[-0.03em] wdth-80 sm:text-6xl'
                                        >
                                            {link.label}
                                            <ArrowRight aria-hidden='true' className='size-7 text-blaze' />
                                        </SmartLink>
                                    </motion.li>
                                ))}
                            </ul>
                        </nav>

                        <motion.div
                            initial={{ y: 24, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className='mt-auto flex flex-col gap-4 pt-12'
                        >
                            <a
                                href={contact.phoneHref}
                                className='inline-flex items-center gap-3 font-heading text-2xl font-bold'
                            >
                                <Phone aria-hidden='true' className='size-5 text-blaze' />
                                {contact.phone}
                            </a>

                            <QuoteLink
                                onClick={closeMenu}
                                className={buttonClass({ size: 'lg', shadow: 'paper', className: 'w-full' })}
                            >
                                {quoteLabel}
                                <ArrowRight aria-hidden='true' className={arrowClass} />
                            </QuoteLink>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    )
}

export default Nav
